import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Plus, MapPin, Clock, CheckCircle2, AlertTriangle, Navigation, ArrowLeft,
  PhoneCall, ChevronRight,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { useReturnTo, withReturn } from "@/lib/returnTo";
import { useGps } from "@/features/gps/useGps";
import { GpsIndicator } from "@/features/gps/GpsIndicator";
import { Card, Pill, Button, Field, Input, Textarea, Select, Spinner, EmptyState } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { fmtDateTime } from "@/lib/dates";
import { useJmpActions, useJmpLive, jmpStandingLine, fmtClock } from "./jmpLive";

// =============================================================================
// JMP (Journey Management Plan) — remote travel safety
// =============================================================================
// A JMP is opened before a long drive to a remote site. It records route,
// ETA, nominated escalation contact. The traveller checks in at configurable
// intervals. If they miss a check-in + grace, the jmp-escalation worker
// fires SMS alerts to the nominated contact.
//
// 22 Oct 2026 (mig 445, road week item 8) — start / check in / complete go
// through app.jmp_start / jmp_check_in / jmp_complete, which put the NEXT
// check-in on the clock every time. Before this the Check in button wrote a
// finished row and never scheduled one, so the escalation worker had nothing
// to watch. The detail page now shows the next due time; the create screen
// takes ?project= from the Site Folder and prefills the project and the site.
// =============================================================================

// -----------------------------------------------------------------------------
// JMP list screen
// -----------------------------------------------------------------------------
export function JmpListScreen() {
  const user = useRequireAuth();
  const [filter, setFilter] = useState<"active" | "mine" | "all">(hasRole(user, "manager") ? "active" : "mine");
  const { data: live = [] } = useJmpLive(!!user);

  const { data: jmps = [], isLoading } = useQuery({
    queryKey: ["jmps", filter, user?.employee_id],
    queryFn: async () => {
      let q = supabase
        .from("journey_management_plans")
        .select(`
          jmp_id, jmp_number, status, depart_location, arrive_location,
          depart_at, eta, return_at,
          traveller:employees!journey_management_plans_traveller_employee_id_fkey(first_name, last_name),
          projects(project_code)
        `)
        .order("depart_at", { ascending: false })
        .limit(100);
      if (filter === "active") q = q.in("status", ["planned", "active"]);
      if (filter === "mine" && user?.employee_id) q = q.eq("traveller_employee_id", user.employee_id);
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Journey Management</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Open a JMP before driving to a remote or marginal-signal site. Missed check-ins auto-escalate.
          </div>
        </div>
        <Link to="/jmp/new"><Button icon={Plus}>New JMP</Button></Link>
      </div>

      <div className="flex items-center gap-2 mb-4">
        {(["active", "mine", "all"] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 font-semibold border ${
              filter === f ? "bg-accent/12 text-accent border-accent/30" : "bg-ink-raised text-text-muted border-line"
            }`}>{f}</button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : jmps.length === 0 ? (
        <Card><EmptyState title="No journeys" description="Create one to get started." icon={Navigation} /></Card>
      ) : (
        <div className="space-y-2">
          {jmps.map(j => {
            const tone: any = j.status === "escalated" ? "critical" : j.status === "active" ? "live" : j.status === "completed" ? "closed" : "mobilising";
            return (
              <Link key={j.jmp_id} to={`/jmp/${j.jmp_id}`}>
                <Card>
                  <div className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-ink-raised border border-line flex items-center justify-center">
                      {j.status === "escalated" ? <AlertTriangle size={16} className="text-danger" /> : <Navigation size={16} className="text-info" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[11px] text-accent">{j.jmp_number}</span>
                        <Pill label={j.status} tone={tone} small />
                        {j.projects?.project_code && <span className="font-mono text-[11px] text-text-muted">· {j.projects.project_code}</span>}
                      </div>
                      <div className="text-[13px] text-text truncate mt-0.5">
                        {j.traveller?.first_name} {j.traveller?.last_name} — {j.depart_location} → {j.arrive_location}
                      </div>
                      <div className="text-[11px] font-mono text-text-muted mt-0.5">
                        Depart {fmtDateTime(j.depart_at)} · ETA {fmtDateTime(j.eta)}
                      </div>
                      {(() => { const l = live.find(x => x.jmp_id === j.jmp_id); if (!l) return null; const s = jmpStandingLine(l);
                        return <div className={`text-[11px] font-medium mt-0.5 ${s.tone === "danger" ? "text-danger" : s.tone === "warn" ? "text-warn" : s.tone === "ok" ? "text-ok" : "text-text-muted"}`}>{s.text}</div>; })()}
                    </div>
                    <ChevronRight size={14} className="text-text-dim shrink-0" />
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Create JMP screen
// -----------------------------------------------------------------------------
export function JmpNewScreen() {
  const returnTo = useReturnTo();   // 27 Sep — opened from a Site folder? go back there
  const [sp] = useSearchParams();
  const projectFromFolder = sp.get("project");   // mig 445 — the Site Folder passes its project
  const user = useRequireAuth();
  const navigate = useNavigate();
  const gps = useGps(true);

  const [depart_location, setDepart] = useState("");
  const [arrive_location, setArrive] = useState("");
  const [depart_at, setDepartAt] = useState(() => nowLocal());
  const [eta, setEta] = useState(() => nowLocal(4));
  const [checkin_interval_minutes, setInterval] = useState(180);
  const [escalation_grace_minutes, setGrace] = useState(15);
  const [escalate_to_employee_id, setEscalateEmployeeId] = useState<string | null>(null);
  const [escalate_to_phone, setEscalatePhone] = useState("");
  const [escalate_director, setEscalateDirector] = useState(true);
  const [route_notes, setRouteNotes] = useState("");
  const [project_id, setProjectId] = useState<string | null>(projectFromFolder);

  const { data: projects = [] } = useQuery({
    queryKey: ["projects_for_jmp"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("projects").select("project_id, project_code, project_name, site_id, site:site_id(site_name, address_line1, suburb, state)")
        .in("status", ["live", "mobilising", "acceptance_pending"]).order("project_code");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // Arriving from the Site Folder: fill "Arrive at" with the site so the
  // rigger types nothing but the depart point (which the GPS offers).
  useEffect(() => {
    if (!project_id || arrive_location) return;
    const p = projects.find((x: any) => x.project_id === project_id);
    const site = p?.site;
    if (site) setArrive([site.site_name, site.address_line1, site.suburb, site.state].filter(Boolean).join(", "));
    else if (p) setArrive(`${p.project_code} · ${p.project_name}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project_id, projects]);

  const { data: employees = [] } = useQuery({
    queryKey: ["employees_for_escalate"],
    queryFn: async () => {
      const { data, error } = await supabase.from("v_employee_names").select("employee_id, full_name, is_active").eq("is_active", true).order("full_name");
      if (error) throw error;
      return data;
    },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      if (!user?.employee_id) throw new Error("no_employee");
      const { data, error } = await supabase
        .from("journey_management_plans")
        .insert({
          project_id,
          site_id: (projects.find((p: any) => p.project_id === project_id) as any)?.site_id ?? null,
          traveller_employee_id: user.employee_id,
          depart_location, depart_at,
          arrive_location, eta,
          checkin_interval_minutes, escalation_grace_minutes,
          escalate_to_employee_id,
          escalate_to_phone: escalate_to_phone || null,
          escalate_director,
          route_notes: route_notes || null,
          status: "planned",
        })
        .select("jmp_id").single();
      if (error) throw error;
      return data.jmp_id;
    },
    onSuccess: (id) => navigate(withReturn(`/jmp/${id}`, returnTo)),
  });

  const canSubmit = depart_location && arrive_location && depart_at && eta && (escalate_to_employee_id || escalate_to_phone);

  return (
    <div className="max-w-lg mx-auto p-4 pb-32">
      <button onClick={() => navigate(returnTo ?? "/jmp")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> {returnTo ? "Site folder" : "JMP list"}
      </button>

      <h1 className="text-[22px] font-semibold tracking-tight mb-1">New Journey Plan</h1>
      <div className="flex items-center gap-3 text-[11px] text-text-muted mb-4">
        <GpsIndicator gps={gps} compact />
      </div>

      <Card>
        <div className="p-5 space-y-4">
          <Field label="Project (optional)">
            <Select value={project_id ?? ""} onChange={e => setProjectId(e.target.value || null)}>
              <option value="">— none —</option>
              {projects.map((p: any) => <option key={p.project_id} value={p.project_id}>{p.project_code} · {p.project_name}</option>)}
            </Select>
          </Field>
          <Field label="Depart from">
            <Input value={depart_location} onChange={e => setDepart(e.target.value)} placeholder="Brisbane yard"
              onBlur={() => { if (!depart_location && gps.fix) setDepart(`${gps.fix.latitude.toFixed(4)}, ${gps.fix.longitude.toFixed(4)}`); }}
            />
          </Field>
          <Field label="Arrive at">
            <Input value={arrive_location} onChange={e => setArrive(e.target.value)} placeholder="e.g. Mackay tower — TWBA-ROOF-01" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Depart"><Input type="datetime-local" mono value={depart_at} onChange={e => setDepartAt(e.target.value)} /></Field>
            <Field label="ETA"><Input type="datetime-local" mono value={eta} onChange={e => setEta(e.target.value)} /></Field>
          </div>

          <div className="pt-3 border-t border-line space-y-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted">Check-in schedule</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Interval (min)" hint="Every 3 hrs is typical for remote drives.">
                <Input type="number" mono value={checkin_interval_minutes} onChange={e => setInterval(parseInt(e.target.value, 10) || 180)} />
              </Field>
              <Field label="Grace (min)" hint="Before escalation fires.">
                <Input type="number" mono value={escalation_grace_minutes} onChange={e => setGrace(parseInt(e.target.value, 10) || 15)} />
              </Field>
            </div>
          </div>

          <div className="pt-3 border-t border-line space-y-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted">Escalation</div>
            <Field label="Primary contact (Coretel employee)">
              <Select value={escalate_to_employee_id ?? ""} onChange={e => setEscalateEmployeeId(e.target.value || null)}>
                <option value="">— none —</option>
                {employees.map((e: any) => <option key={e.employee_id} value={e.employee_id}>{e.full_name}</option>)}
              </Select>
            </Field>
            <Field label="Extra phone (external)" hint="Spouse, partner, external emergency contact.">
              <Input mono value={escalate_to_phone} onChange={e => setEscalatePhone(e.target.value)} placeholder="+61 4xx xxx xxx" />
            </Field>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={escalate_director} onChange={e => setEscalateDirector(e.target.checked)} className="accent-accent" />
              <span className="text-[13px] text-text">Also escalate to a director</span>
            </label>
          </div>

          <Field label="Route notes (optional)">
            <Textarea rows={3} value={route_notes} onChange={e => setRouteNotes(e.target.value)} placeholder="Via Warrego Hwy, no mobile after Dalby, fuel at Chinchilla…" />
          </Field>
        </div>
      </Card>

      <StickyBottomBar className="p-3 flex justify-center">
        <div className="flex gap-2 max-w-lg w-full">
          <Button variant="secondary" size="lg" onClick={() => navigate(returnTo ?? "/jmp")}>Cancel</Button>
          <Button size="lg" icon={CheckCircle2} onClick={() => createMut.mutate()} disabled={!canSubmit || createMut.isPending} className="flex-1 justify-center">
            {createMut.isPending ? "Saving…" : "Save plan"}
          </Button>
        </div>
      </StickyBottomBar>
    </div>
  );
}

// -----------------------------------------------------------------------------
// JMP detail + check-in
// -----------------------------------------------------------------------------
export function JmpDetailScreen() {
  const returnTo = useReturnTo();
  const { jmpId } = useParams<{ jmpId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const gps = useGps(true);

  const { data: jmp, isLoading, refetch } = useQuery({
    queryKey: ["jmp", jmpId],
    enabled: !!jmpId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("journey_management_plans")
        .select(`
          jmp_id, jmp_number, status, depart_location, arrive_location,
          depart_at, eta, return_at, actual_return_at,
          checkin_interval_minutes, escalation_grace_minutes,
          escalate_to_phone, route_notes,
          traveller_employee_id,
          traveller:employees!journey_management_plans_traveller_employee_id_fkey(first_name, last_name, mobile),
          escalate_to:employees!journey_management_plans_escalate_to_employee_id_fkey(first_name, last_name, mobile),
          projects(project_code)
        `)
        .eq("jmp_id", jmpId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    refetchInterval: 60_000,
  });

  const { data: checkins = [] } = useQuery({
    queryKey: ["jmp_checkins", jmpId],
    enabled: !!jmpId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jmp_check_ins")
        .select("checkin_id, scheduled_at, checked_in_at, status, notes, gps_lat, gps_lng")
        .eq("jmp_id", jmpId!)
        .order("scheduled_at", { ascending: true });
      if (error) throw error;
      return data as any[];
    },
    refetchInterval: 60_000,
  });

  // mig 445 — the three actions are database functions; they schedule the
  // next check-in and the escalation worker watches it.
  const { start, checkIn, complete } = useJmpActions();
  const { data: live = [] } = useJmpLive(!!jmpId);
  const liveRow = live.find(l => l.jmp_id === jmpId) ?? null;
  const onErr = (e: any) => window.alert(e?.message ?? "That didn't save");
  const checkinMut = { isPending: start.isPending || checkIn.isPending, mutate: () => {
    if (!jmpId) return;
    if (jmp?.status === "planned") start.mutate({ jmp_id: jmpId }, { onSuccess: () => refetch(), onError: onErr });
    else checkIn.mutate({ jmp_id: jmpId, lat: gps.fix?.latitude ?? null, lng: gps.fix?.longitude ?? null }, { onSuccess: () => refetch(), onError: onErr });
  } };
  const completeMut = { isPending: complete.isPending, mutate: () => { if (jmpId) complete.mutate({ jmp_id: jmpId }, { onSuccess: () => refetch(), onError: onErr }); } };

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner /></div>;
  if (!jmp) return <div className="p-6">JMP not found.</div>;

  const isMine = jmp.traveller_employee_id === user?.employee_id;
  const canOperate = isMine || hasRole(user, "manager");
  const active = jmp.status === "active" || jmp.status === "planned" || jmp.status === "escalated";
  const standing = liveRow ? jmpStandingLine(liveRow) : null;
  const standingCls = standing?.tone === "danger" ? "text-danger" : standing?.tone === "warn" ? "text-warn" : standing?.tone === "ok" ? "text-ok" : "text-text-muted";

  return (
    <div className="max-w-lg mx-auto p-4 pb-32">
      <button onClick={() => navigate(returnTo ?? "/jmp")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> {returnTo ? "Site folder" : "JMP list"}
      </button>

      <div className="mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[13px] text-accent">{jmp.jmp_number}</span>
          <Pill label={jmp.status} tone={jmp.status === "escalated" ? "critical" : jmp.status === "active" ? "live" : jmp.status === "completed" ? "closed" : "mobilising"} />
        </div>
        <h1 className="text-[22px] font-semibold tracking-tight mt-1">
          {jmp.depart_location} → {jmp.arrive_location}
        </h1>
        <div className="text-text-muted text-[13px] mt-1">
          {jmp.traveller?.first_name} {jmp.traveller?.last_name} · ETA {fmtDateTime(jmp.eta)}
        </div>
      </div>

      {/* Prominent check-in card for the traveller */}
      {canOperate && active && (
        <Card className="mb-4" style={{ background: "linear-gradient(135deg, rgba(255,106,26,0.08), rgba(255,106,26,0.02))" }}>
          <div className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
              <Clock size={22} className="text-accent" />
            </div>
            <div className="flex-1">
              <div className="text-[13px] text-text font-medium">{jmp.status === "planned" ? "Setting off? Start the journey" : "Ready to check in"}</div>
              {standing && <div className={`text-[12px] font-medium mt-0.5 ${standingCls}`}>{standing.text}</div>}
              <div className="text-[11px] text-text-muted mt-0.5">
                <GpsIndicator gps={gps} compact />
              </div>
            </div>
            <Button icon={CheckCircle2} onClick={() => checkinMut.mutate()} disabled={checkinMut.isPending}>
              {checkinMut.isPending ? "Saving…" : jmp.status === "planned" ? "Start journey" : "Check in"}
            </Button>
          </div>
        </Card>
      )}

      {/* Check-in history */}
      <Card className="mb-4">
        <div className="p-4 border-b border-line flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Check-ins</div>
          <div className="text-[11px] font-mono text-text-muted">Every {jmp.checkin_interval_minutes} min</div>
        </div>
        {checkins.length === 0 ? (
          <div className="p-5 text-center text-[12px] text-text-muted">Nothing on the clock yet. Tap Start journey when you set off — the first check-in is due {jmp.checkin_interval_minutes} min later.</div>
        ) : (
          checkins.map((c, i) => (
            <div key={c.checkin_id} className={`px-4 py-3 flex items-center gap-3 ${i < checkins.length - 1 ? "border-b border-line" : ""}`}>
              {c.status === "on_time" ? <CheckCircle2 size={14} className="text-ok" /> :
               c.status === "late"   ? <Clock size={14} className="text-warn" /> :
               c.status === "missed" || c.status === "escalated" ? <AlertTriangle size={14} className="text-danger" /> :
               <Clock size={14} className="text-text-muted" />}
              <div className="flex-1">
                <div className="text-[13px] text-text">
                  {c.status === "scheduled" ? `Due ${fmtClock(c.scheduled_at)}` : `Scheduled ${fmtDateTime(c.scheduled_at)}`}
                </div>
                <div className="text-[11px] text-text-muted font-mono">
                  {c.checked_in_at ? `Checked in ${fmtDateTime(c.checked_in_at)}${c.gps_lat != null ? " · GPS" : ""}` : c.status === "scheduled" ? "waiting" : "—"}
                </div>
              </div>
              <Pill label={c.status === "on_time" ? "on time" : c.status} tone={c.status === "on_time" ? "approved" : c.status === "late" ? "warning" : c.status === "missed" || c.status === "escalated" ? "critical" : c.status === "cancelled" ? "default" : "pending"} small />
            </div>
          ))
        )}
      </Card>

      {/* Complete journey */}
      {canOperate && active && (
        <Card>
          <div className="p-4 flex items-center justify-between">
            <div>
              <div className="text-[13px] text-text">Finished safely?</div>
              <div className="text-[11px] text-text-muted">Close the plan so we stop watching for check-ins.</div>
            </div>
            <Button variant="secondary" onClick={() => completeMut.mutate()} disabled={completeMut.isPending}>Complete</Button>
          </div>
        </Card>
      )}

      {jmp.escalate_to && (
        <div className="mt-4 text-[11px] text-text-muted flex items-center gap-2">
          <PhoneCall size={11} /> Escalation contact: {jmp.escalate_to.first_name} {jmp.escalate_to.last_name}
        </div>
      )}
    </div>
  );
}

function nowLocal(offsetHours = 0): string {
  const d = new Date(Date.now() + offsetHours * 3600_000);
  d.setSeconds(0, 0);
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
}
